import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import ClipImage from "@/components/ClipImage";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { tr } = useLang();

  useEffect(() => {
    api
      .get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-white text-[#0a0a0a]">
        <h2 className="font-display text-6xl tracking-tighter">
          {tr({ it: "Articolo non trovato", en: "Article not found" })}
        </h2>
        <Link to="/blog" className="btn-ghost">{tr({ it: "Torna al blog", en: "Back to blog" })}</Link>
      </div>
    );
  }

  if (!post) return <div className="min-h-screen bg-white" />;

  return (
    <article data-testid="page-blog-post" className="bg-white text-[#0a0a0a] pt-32 pb-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] hover:text-[#007A8A] transition-colors mb-12"
          data-testid="blog-back"
        >
          <ArrowLeft size={14} /> {tr({ it: "Torna al blog", en: "Back to blog" })}
        </Link>

        <div className="flex items-center gap-4 font-mono text-[10px] tracking-widest uppercase text-[#007A8A] mb-8">
          <span>{post.category}</span>
          <span className="text-[#52525b]">·</span>
          <span className="text-[#3f3f46]">{post.read_minutes} min</span>
        </div>

        <h1 className="font-display text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-tighter mb-12">
          {post.title}
        </h1>

        <div className="font-editorial italic text-2xl md:text-3xl text-[#3f3f46] leading-relaxed max-w-3xl mb-16">
          {post.excerpt}
        </div>

        {post.cover_image && (
          <ClipImage
            src={post.cover_image}
            alt={post.title}
            className="w-full aspect-[16/9] border border-[#e4e4e7] mb-16"
          />
        )}

        <Reveal>
          <div className="font-body text-lg text-[#0a0a0a] leading-[1.8] whitespace-pre-line max-w-3xl">
            {post.content}
          </div>
        </Reveal>

        <div className="mt-20 pt-8 border-t border-[#e4e4e7] flex items-center justify-between font-mono text-[10px] tracking-widest uppercase text-[#52525b]">
          <div>— {post.author}</div>
          <div>{new Date(post.created_at).toLocaleDateString("it-IT", { year: "numeric", month: "long" })}</div>
        </div>
      </div>
    </article>
  );
}
